import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { NumericInput } from "@/components/ui/numeric-input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, HelpCircle, ClipboardList } from "lucide-react";
import { Pro013CampaignData } from "../create-pro013-form";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "@/components/ui/use-toast";

interface StepThreeDiscountProps {
  data: Pro013CampaignData;
  updateData: (data: Partial<Pro013CampaignData>) => void;
  nextStep: () => void;
  prevStep: () => void;
}

// Tipos de desconto conforme documentação
const discountTypes = [
  { value: 0, label: "Tipo 0 - Desconto Integral (Menor Valor)", description: "O item de menor valor sai de graça" },
  { value: 1, label: "Tipo 1 - Percentual (Menor Valor)", description: "Percentual de desconto no item de menor valor" },
  { value: 2, label: "Tipo 2 - Valor Fixo (Menor Valor)", description: "Valor fixo de desconto no item de menor valor" },
  { value: 3, label: "Tipo 3 - Desconto Integral", description: "Desconto integral aplicado" },
  { value: 4, label: "Tipo 4 - Percentual", description: "Percentual de desconto" },
  { value: 5, label: "Tipo 5 - Valor Fixo", description: "Valor fixo de desconto" },
  { value: 7, label: "Tipo 7 - Brinde", description: "Item como brinde" },
  { value: 8, label: "Tipo 8 - Brinde (Mesmo Item)", description: "Mesmo item como brinde" },
];

export default function StepThreeDiscount({ data, updateData, nextStep, prevStep }: StepThreeDiscountProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Tipos que exigem discountValue > 0: 1 (Percentual Menor Valor), 2 (Valor Fixo Menor Valor), 4 (Percentual), 5 (Valor Fixo)
  // Tipos que permitem discountValue = 0: 0, 3, 7, 8 (desconto integral e brinde)
  const typesRequiringDiscountValue = [1, 2, 4, 5];

  const isFormValid = useMemo(() => {
    const hasValidTakeQuantity = data.takeQuantity >= 1;
    // STORY-NEW-041: Permitir pague <= leve (cenário 5 - valores iguais resultam em diferença 0)
    const hasValidPayQuantity = data.payQuantity >= 1 && data.payQuantity <= data.takeQuantity;
    // Verifica se o tipo de desconto exige valor > 0
    const requiresDiscountValue = [1, 2, 4, 5].includes(data.discountType);
    const hasValidDiscountValue = requiresDiscountValue
      ? data.discountValue > 0
      : data.discountValue >= 0;
    return hasValidTakeQuantity && hasValidPayQuantity && hasValidDiscountValue;
  }, [data.takeQuantity, data.payQuantity, data.discountValue, data.discountType]);

  const validateStep = (): boolean => {
    const newErrors: Record<string, string> = {};

    // STORY-NEW-041: Pague <= Leve (permite valores iguais - cenário 5)
    if (data.payQuantity > data.takeQuantity) {
      newErrors.payQuantity = "Quantidade 'Pague' deve ser menor ou igual a 'Leve'";
    }

    if (data.takeQuantity < 1) {
      newErrors.takeQuantity = "Quantidade 'Leve' deve ser no mínimo 1";
    }

    if (data.payQuantity < 1) {
      newErrors.payQuantity = "Quantidade 'Pague' deve ser no mínimo 1";
    }

    // Valor/Percentual: validação condicional baseada no tipo de desconto
    const requiresDiscountValue = typesRequiringDiscountValue.includes(data.discountType);
    if (requiresDiscountValue && data.discountValue <= 0) {
      newErrors.discountValue = "Para o tipo de desconto selecionado, o valor/percentual deve ser maior que 0";
    } else if (data.discountValue < 0) {
      newErrors.discountValue = "Valor/Percentual não pode ser negativo";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      nextStep();
    } else {
      toast({
        variant: "destructive",
        title: "Erro de validação",
        description: "Verifique os campos obrigatórios antes de continuar.",
      });
    }
  };

  const selectedDiscountType = discountTypes.find(t => t.value === data.discountType);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Passo 3: Configuração de Desconto</CardTitle>
        <CardDescription>
          Defina as regras de desconto da campanha (Leve X Pague Y)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Tipo de Desconto */}
        <div className="space-y-2">
          <Label htmlFor="discountType">
            Tipo de Desconto <span className="text-red-500">*</span>
          </Label>
          <Select
            value={data.discountType.toString()}
            onValueChange={(value) => updateData({ discountType: parseInt(value) })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione o tipo de desconto" />
            </SelectTrigger>
            <SelectContent>
              {discountTypes.map((type) => (
                <SelectItem key={type.value} value={type.value.toString()}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedDiscountType && (
            <p className="text-sm text-gray-600">
              <HelpCircle className="inline h-4 w-4 mr-1" />
              {selectedDiscountType.description}
            </p>
          )}
        </div>

        {/* Leve e Pague */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Label htmlFor="takeQuantity" className="cursor-help">
                    Quantidade "Leve" <span className="text-red-500">*</span>
                    <HelpCircle className="inline h-4 w-4 ml-1" />
                  </Label>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Quantidade de produtos que o cliente deve levar</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <NumericInput
              id="takeQuantity"
              value={data.takeQuantity}
              onChange={(val) => updateData({ takeQuantity: val })}
              min={1}
              max={99}
              maxLength={2}
              placeholder="Ex: 3"
              hasError={!!errors.takeQuantity}
            />
            {errors.takeQuantity && (
              <p className="text-sm text-red-500">{errors.takeQuantity}</p>
            )}
          </div>

          <div className="space-y-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Label htmlFor="payQuantity" className="cursor-help">
                    Quantidade "Pague" <span className="text-red-500">*</span>
                    <HelpCircle className="inline h-4 w-4 ml-1" />
                  </Label>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Quantidade de produtos que o cliente deve pagar</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <NumericInput
              id="payQuantity"
              value={data.payQuantity}
              onChange={(val) => updateData({ payQuantity: val })}
              min={1}
              max={99}
              maxLength={2}
              placeholder="Ex: 2"
              hasError={!!errors.payQuantity}
            />
            {errors.payQuantity && (
              <p className="text-sm text-red-500">{errors.payQuantity}</p>
            )}
          </div>
        </div>

        {/* Valor/Percentual de Desconto */}
        <div className="space-y-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Label htmlFor="discountValue" className="cursor-help">
                  Valor/Percentual de Desconto <span className="text-red-500">*</span>
                  <HelpCircle className="inline h-4 w-4 ml-1" />
                </Label>
              </TooltipTrigger>
              <TooltipContent>
                <p>Para percentual (tipos 1, 4): use valores entre 1-100</p>
                <p>Para valor fixo (tipos 2, 5): use o valor em reais (ex: 10.50)</p>
                <p>Para desconto integral ou brinde (tipos 0, 3, 7, 8): valor 0 é permitido</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <NumericInput
            id="discountValue"
            value={data.discountValue}
            onChange={(val) => updateData({ discountValue: val })}
            min={0}
            max={9999.99}
            maxLength={7}
            step={0.01}
            numberType="decimal"
            placeholder="0"
            hasError={!!errors.discountValue}
          />
          {errors.discountValue && (
            <p className="text-sm text-red-500">{errors.discountValue}</p>
          )}
        </div>

        {/* Aplicar em toda loja */}
        <div className="space-y-2">
          <Label htmlFor="allStoreFlag">Aplicar em toda a loja?</Label>
          <Select
            value={data.allStoreFlag.toString()}
            onValueChange={(value) => updateData({ allStoreFlag: parseInt(value) })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Não (0)</SelectItem>
              <SelectItem value="1">Sim (1)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-gray-600">
            Define se a promoção vale para todos os produtos da loja ou apenas departamentos/grupos específicos
          </p>
        </div>

        {/*
          CAMPO REMOVIDO: Forma de Pagamento
          Data: 2025-11-19
          Motivo: Simplificação do processo - sempre usar "Todas as Formas" (valor 0)
          Story: STORY-NEW-024

          Para reverter: Descomentar este bloco e remover default paymentMethod: 0 no handleNext
        */}
        {/* <div className="space-y-2">
          <Label htmlFor="paymentMethod">Forma de Pagamento</Label>
          <Select
            value={paymentMethod.toString()}
            onValueChange={(value) => setPaymentMethod(parseInt(value))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Todas (0)</SelectItem>
              <SelectItem value="1">Dinheiro (1)</SelectItem>
              <SelectItem value="2">Cartão de Crédito (2)</SelectItem>
              <SelectItem value="3">Cartão de Débito (3)</SelectItem>
              <SelectItem value="4">PIX (4)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-gray-600">
            Restringe a promoção a uma forma de pagamento específica
          </p>
        </div> */}

        {/* Preview da Regra */}
        <div className="bg-green-50 p-4 rounded-md border border-green-200">
          <p className="text-sm font-medium text-green-800 flex items-center gap-1">
            <ClipboardList className="h-4 w-4" /> Resumo da Regra:
          </p>
          <p className="text-sm text-green-700 mt-2">
            Cliente <strong>leva {data.takeQuantity}</strong> produto(s) dos departamentos/grupos selecionados
            e <strong>paga {data.payQuantity}</strong> produto(s).
            {data.discountValue > 0 && (
              <span> Desconto de <strong>{data.discountValue}</strong> {data.discountType === 1 || data.discountType === 4 ? "%" : "R$"}.</span>
            )}
          </p>
        </div>

        {/* Botões */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={prevStep}>
            <ChevronLeft className="mr-2 h-4 w-4" /> Voltar
          </Button>
          <Button onClick={handleNext} disabled={!isFormValid}>
            Próximo <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
